import Vue from 'vue'
import Vuex from 'vuex'
import { menuTree, findParentIndex, getChildrenByIndex } from '@/config/menu'
import { getToken, setToken, removeToken } from '@/utils/auth'
import { vendors as seedVendors, products as seedProducts, seedLogs, reportOrders as seedReportOrders, reverseApps as seedReverseApps, shipOrders as seedShipOrders, qualifications as seedQualifications, contracts as seedContracts, contractTemplates as seedContractTemplates, shutdowns as seedShutdowns, invoices as seedInvoices, aftersales as seedAftersales, biddings as seedBiddings, inquiries as seedInquiries, messages as seedMessages, accounts as seedAccounts, roles as seedRoles, brandConfig as seedBrandConfig, serviceAgents as seedServiceAgents, serviceSchedules as seedServiceSchedules, serviceAssignLogs as seedServiceAssignLogs, serviceConfig as seedServiceConfig, helpCategories as seedHelpCategories, helpArticles as seedHelpArticles, helpFeedbacks as seedHelpFeedbacks, guideConfigs as seedGuideConfigs, exceptionTickets as seedExceptionTickets, voucherReviews as seedVoucherReviews, exceptionTypes as seedExceptionTypes, exceptionSla as seedExceptionSla } from '@/mock/data'

function nowStr() {
  const d = new Date()
  const p = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

/** 根据角色 key 取该角色可见的一级菜单路径集合 */
function getRolePaths(roleKey) {
  const r = seedRoles.find(x => x.key === roleKey)
  return r && r.menuPaths ? r.menuPaths : ['/dashboard']
}

/** 按一级菜单路径集合过滤整棵菜单树 */
function buildMenuTree(paths) {
  if (!paths || !paths.length) return []
  return menuTree.filter(m => paths.includes(m.path))
}

/** 从 sessionStorage 恢复已登录用户（刷新后保持角色/菜单） */
function loadSavedUser() {
  try {
    return JSON.parse(window.sessionStorage.getItem('manager_user'))
  } catch (e) {
    return null
  }
}

Vue.use(Vuex)

const VISITED_VIEWS_KEY = 'manager_visited_views'

function loadVisitedViews() {
  try {
    const raw = window.sessionStorage.getItem(VISITED_VIEWS_KEY)
    return raw ? JSON.parse(raw) : []
  } catch (e) {
    return []
  }
}

function saveVisitedViews(list) {
  try {
    window.sessionStorage.setItem(VISITED_VIEWS_KEY, JSON.stringify(list))
  } catch (e) {
    // ignore storage error
  }
}

const savedUser = loadSavedUser()

export default new Vuex.Store({
  state: {
    // 一级菜单数据（登录后按角色过滤）
    menuList: savedUser && savedUser.roleKey ? buildMenuTree(getRolePaths(savedUser.roleKey)) : menuTree,
    // 供应商档案（列表用，消息发送等模块作为收件人来源）
    vendors: JSON.parse(JSON.stringify(seedVendors)),
    // 当前激活的一级菜单 index (默认 0 = 工作台)
    activeMenuIndex: 0,
    // 当前二级菜单列表
    childMenuList: savedUser && savedUser.roleKey ? (buildMenuTree(getRolePaths(savedUser.roleKey))[0] || {}).children || [] : (menuTree[0].children || []),
    // 二级菜单是否收起（columns 模式下无子菜单或只有一个时自动收起）
    isCollapse: false,
    // 登录 Token
    token: getToken(),
    // 用户信息（mock，登录后携带角色/数据范围/级别）
    userInfo: savedUser || {
      name: '平台管理员',
      avatar: '',
      phone: '',
      company: '安徽亿佳供应链集团',
    },
    // 账号数据源（8 个预置 + 可自建）
    accounts: JSON.parse(JSON.stringify(seedAccounts)),
    // 品牌人格化配置（原则1：小亿，后台可配置）
    brandConfig: JSON.parse(JSON.stringify(seedBrandConfig)),
    // 已打开的页面标签（顶部 TagsView），刷新后从 sessionStorage 恢复
    visitedViews: loadVisitedViews(),
    // 商品（管理端商品中心数据源：审核/下架/重提后状态在此更新，形成「提交→审核→通过→下架」闭环）
    products: JSON.parse(JSON.stringify(seedProducts)),
    // 报备订单（管理端报备审核台数据源：审核通过后状态在此更新）
    reportOrders: JSON.parse(JSON.stringify(seedReportOrders)),
    // 反采申请（管理端反采审核台数据源：审核通过后状态在此更新）
    reverseApps: JSON.parse(JSON.stringify(seedReverseApps)),
    // 发货单（管理端发货单管理数据源：妥投/拒收处理后状态在此更新）
    shipOrders: JSON.parse(JSON.stringify(seedShipOrders)),
    // 资质/授权审核（数据源）
    qualifications: JSON.parse(JSON.stringify(seedQualifications)),
    // 合同管理（数据源）
    contracts: JSON.parse(JSON.stringify(seedContracts)),
    // 合同模板库（数据源）
    contractTemplates: JSON.parse(JSON.stringify(seedContractTemplates)),
    // 供应商关停审批（数据源）
    shutdowns: JSON.parse(JSON.stringify(seedShutdowns)),
    // 发票管理（数据源）
    invoices: JSON.parse(JSON.stringify(seedInvoices)),
    // 售后服务管理（数据源）
    aftersales: JSON.parse(JSON.stringify(seedAftersales)),
    // 采购竞价管理（数据源）
    biddings: JSON.parse(JSON.stringify(seedBiddings)),
    // 询价单管理（数据源）
    inquiries: JSON.parse(JSON.stringify(seedInquiries)),
    // 消息中心（数据源）
    messages: JSON.parse(JSON.stringify(seedMessages)),
    // 角色权限（数据源）
    roles: JSON.parse(JSON.stringify(seedRoles)),
    // 操作日志（全平台审计追踪，各模块审核/治理动作均写入此处）
    logs: JSON.parse(JSON.stringify(seedLogs)),
    logSeq: 0,
    // 客服系统（M1）：客服档案 / 排班 / 分配日志 / 轮询策略
    serviceAgents: JSON.parse(JSON.stringify(seedServiceAgents)),
    serviceSchedules: JSON.parse(JSON.stringify(seedServiceSchedules)),
    serviceAssignLogs: JSON.parse(JSON.stringify(seedServiceAssignLogs)),
    serviceConfig: JSON.parse(JSON.stringify(seedServiceConfig)),
    // 帮助中心（M2）：分类 / 文章 / 反馈（原则3：内容运营自助化）
    helpCategories: JSON.parse(JSON.stringify(seedHelpCategories)),
    helpArticles: JSON.parse(JSON.stringify(seedHelpArticles)),
    helpFeedbacks: JSON.parse(JSON.stringify(seedHelpFeedbacks)),
    // 页面引导（M3）：按路由绑定的贴身提示配置（原则3 内容运营自助化）
    guideConfigs: JSON.parse(JSON.stringify(seedGuideConfigs)),
    // 结算异常（M4）：异常工单（原则4 异常工单化）
    exceptionTickets: JSON.parse(JSON.stringify(seedExceptionTickets)),
    exceptionTypes: JSON.parse(JSON.stringify(seedExceptionTypes)),
    exceptionSla: JSON.parse(JSON.stringify(seedExceptionSla)),
    // 签收凭证审核（M5）：审核工单（原则4 审核工单化）
    voucherReviews: JSON.parse(JSON.stringify(seedVoucherReviews)),
  },

  getters: {
    currentMenuTitle: (state) => {
      const item = state.menuList[state.activeMenuIndex]
      return item ? item.title : ''
    },
    hasChildren: (state) => {
      return state.childMenuList && state.childMenuList.length > 0
    },
    /** 当前用户角色可见的一级菜单路径集合（用于路由守卫与菜单渲染） */
    allowedTopPaths: (state) => {
      const u = state.userInfo
      if (!u || !u.roleKey) return []
      const r = state.roles.find(x => x.key === u.roleKey)
      return r && r.menuPaths ? r.menuPaths : []
    },
    /** 当前登录用户完整信息 */
    currentUser: (state) => state.userInfo,
    /** 人格化昵称（原则1：小亿，后台可配置，默认值小亿） */
    personaName: (state) => (state.brandConfig && state.brandConfig.personaName) || '小亿',
  },

  mutations: {
    SET_TOKEN(state, token) {
      state.token = token
    },

    SET_USER_INFO(state, info) {
      state.userInfo = { ...state.userInfo, ...info }
    },

    /** 切换一级菜单 */
    SET_ACTIVE_MENU_INDEX(state, index) {
      state.activeMenuIndex = index
      state.childMenuList = getChildrenByIndex(state.menuList, index)
      // 无子菜单或只有1个时自动收起
      const children = state.childMenuList
      state.isCollapse = !children || children.length <= 1
    },

    /** 设置子菜单列表 */
    SET_CHILD_MENU_LIST(state, list) {
      state.childMenuList = list
      state.isCollapse = !list || list.length <= 1
    },

    /** 切换折叠 */
    TOGGLE_COLLAPSE(state) {
      state.isCollapse = !state.isCollapse
    },

    /** TagsView：添加已访问视图 */
    ADD_VISITED_VIEW(state, view) {
      if (state.visitedViews.some(v => v.path === view.path)) return
      state.visitedViews.push({
        path: view.path,
        title: view.meta.title || '未命名',
        fullPath: view.fullPath || view.path,
      })
      saveVisitedViews(state.visitedViews)
    },

    /** TagsView：删除指定视图 */
    DEL_VISITED_VIEW(state, view) {
      for (const [i, v] of state.visitedViews.entries()) {
        if (v.path === view.path) {
          state.visitedViews.splice(i, 1)
          break
        }
      }
      saveVisitedViews(state.visitedViews)
    },

    /** TagsView：删除其他视图 */
    DEL_OTHERS_VISITED_VIEWS(state, view) {
      state.visitedViews = state.visitedViews.filter(v => {
        return v.path === view.path || (v.meta && v.meta.affix)
      })
      saveVisitedViews(state.visitedViews)
    },

    /** TagsView：删除全部视图 */
    DEL_ALL_VISITED_VIEWS(state) {
      state.visitedViews = []
      saveVisitedViews(state.visitedViews)
    },

    /** 商品：更新审核状态（通过/驳回/打回重提/下架） */
    SET_PRODUCT_STATUS(state, { id, status, reason }) {
      const p = state.products.find(x => x.id === Number(id))
      if (p) {
        p.auditStatus = status
        if (reason !== undefined) p.rejectReason = reason
        p.lastAuditTime = nowStr()
      }
    },

    /** 操作日志：写入一条记录（unshift 置顶） */
    ADD_LOG(state, log) {
      state.logSeq += 1
      state.logs.unshift({
        id: state.logSeq,
        time: log.time || nowStr(),
        module: log.module || '系统',
        action: log.action || '',
        target: log.target || '',
        operator: log.operator || '平台管理员',
        detail: log.detail || '',
      })
    },

    /** 报备订单：更新审核状态（通过/驳回） */
    SET_REPORT_STATUS(state, { id, status, reason }) {
      const r = state.reportOrders.find(x => x.id === Number(id))
      if (r) {
        r.reportStatus = status
        r.auditStatus = status
        if (reason !== undefined) r.rejectReason = reason
        r.auditTime = nowStr()
      }
    },

    /** 反采申请：更新审核状态（通过/驳回） */
    SET_REVERSE_STATUS(state, { id, status, reason }) {
      const r = state.reverseApps.find(x => x.id === Number(id))
      if (r) {
        r.status = status
        if (reason !== undefined) r.rejectReason = reason
        r.auditTime = nowStr()
      }
    },

    /** 发货单：更新妥投/拒收处理状态 */
    SET_SHIP_STATUS(state, { id, deliveredStatus, rejectStatus }) {
      const s = state.shipOrders.find(x => x.id === Number(id))
      if (s) {
        if (deliveredStatus !== undefined) s.deliveredStatus = deliveredStatus
        if (deliveredStatus === '已妥投') { s.status = '已妥投'; s.deliveredTime = nowStr() }
        if (rejectStatus !== undefined) s.rejectStatus = rejectStatus
        if (rejectStatus === '已拒收') s.status = '已拒收'
      }
    },

    /** 资质/授权审核：更新审核状态（通过/驳回） */
    SET_QUALIFICATION_STATUS(state, { id, status, reason }) {
      const q = state.qualifications.find(x => x.id === Number(id))
      if (q) {
        q.auditStatus = status
        if (reason !== undefined) q.remark = reason
        q.updateTime = nowStr()
      }
    },

    /** 合同管理：新建/编辑合同草稿 */
    UPSERT_CONTRACT(state, contract) {
      if (contract.id) {
        const idx = state.contracts.findIndex(x => x.id === Number(contract.id))
        if (idx >= 0) { state.contracts.splice(idx, 1, { ...state.contracts[idx], ...contract }); return }
      }
      const maxId = state.contracts.reduce((mx, c) => Math.max(mx, Number(c.id) || 0), 0)
      state.contracts.unshift({ id: maxId + 1, signStatus: '未发起', contractStatus: '草稿', createdAt: nowStr(), ...contract })
    },
    /** 合同：发起电子签署（写入任务、签署链接、厂商） */
    SEND_CONTRACT_SIGN(state, { id, taskId, signUrl, provider }) {
      const c = state.contracts.find(x => x.id === Number(id))
      if (c) {
        c.signStatus = '签署中'
        c.contractStatus = '签署中'
        c.signTaskId = taskId
        c.signUrl = signUrl
        c.esignProvider = provider
        c.initiatedAt = nowStr()
      }
    },
    /** 合同：签署完成（落章、生成签署件） */
    COMPLETE_CONTRACT_SIGN(state, { id, signedFileUrl, signedAt, signer, doubleStamp }) {
      const c = state.contracts.find(x => x.id === Number(id))
      if (c) {
        c.signStatus = '已签署完成'
        c.contractStatus = '生效中'
        if (c.signType === 2 || doubleStamp) c.doubleStampContractUrl = signedFileUrl
        else c.stampContractUrl = signedFileUrl
        c.signedAt = signedAt || nowStr()
        c.signedBy = signer || ''
        c.lastExpireReminder = ''
        c.lastPaymentReminder = ''
      }
    },
    /** 合同：拒签 */
    REJECT_CONTRACT_SIGN(state, { id, reason }) {
      const c = state.contracts.find(x => x.id === Number(id))
      if (c) { c.signStatus = '已拒签'; c.contractStatus = '待签署'; c.rejectReason = reason || '' }
    },
    /** 合同：历史合同上传补录 */
    UPLOAD_CONTRACT_HISTORY(state, { id, importFile, fields }) {
      const c = state.contracts.find(x => x.id === Number(id))
      if (c) {
        c.isHistory = true
        c.importFile = importFile || c.importFile
        if (fields) Object.assign(c, fields)
        if (c.contractStatus === '草稿' || c.contractStatus === '待签署') c.contractStatus = '生效中'
        if (!c.signStatus || c.signStatus === '未发起') c.signStatus = '已签署完成'
        c.signedAt = c.signedAt || fields.signedAt || nowStr()
      }
    },
    /** 合同：续签（延长有效期） */
    RENEW_CONTRACT(state, { id, newEndDate }) {
      const c = state.contracts.find(x => x.id === Number(id))
      if (c) { c.endDate = newEndDate; c.contractStatus = '生效中'; c.lastExpireReminder = '' }
    },
    /** 合同：终止 */
    TERMINATE_CONTRACT(state, { id, reason }) {
      const c = state.contracts.find(x => x.id === Number(id))
      if (c) { c.contractStatus = '已终止'; c.terminateReason = reason || '' }
    },
    /** 合同：归档 */
    ARCHIVE_CONTRACT(state, { id }) {
      const c = state.contracts.find(x => x.id === Number(id))
      if (c) c.contractStatus = '已归档'
    },
    /** 合同：标记某类提醒已发送 */
    SET_CONTRACT_REMINDER_DONE(state, { id, kind }) {
      const c = state.contracts.find(x => x.id === Number(id))
      if (c) { if (kind === 'expire') c.lastExpireReminder = nowStr(); else c.lastPaymentReminder = nowStr() }
    },
    /** 合同模板：新增/编辑 */
    UPSERT_CONTRACT_TEMPLATE(state, tpl) {
      const idx = state.contractTemplates.findIndex(t => t.id === tpl.id)
      if (idx >= 0) state.contractTemplates.splice(idx, 1, { ...state.contractTemplates[idx], ...tpl })
      else state.contractTemplates.push({ id: Date.now(), enabled: true, updatedAt: nowStr(), ...tpl })
    },
    /** 合同模板：启停 */
    TOGGLE_CONTRACT_TEMPLATE(state, id) {
      const t = state.contractTemplates.find(x => x.id === Number(id))
      if (t) t.enabled = !t.enabled
    },

    /** 供应商关停审批：更新审核状态（通过/驳回） */
    SET_SHUTDOWN_STATUS(state, { id, status, reason }) {
      const s = state.shutdowns.find(x => x.id === Number(id))
      if (s) {
        s.status = status
        s.auditTime = nowStr()
        if (reason !== undefined) s.auditReason = reason
      }
    },

    /** 发票管理：更新开票/作废/下载状态 */
    SET_INVOICE_STATUS(state, { id, invoiceStatus, downloadStatus }) {
      const i = state.invoices.find(x => x.id === Number(id))
      if (i) {
        if (invoiceStatus !== undefined) { i.invoiceStatus = invoiceStatus; if (invoiceStatus === '已开票') i.invoiceTime = nowStr() }
        if (downloadStatus !== undefined) i.downloadStatus = downloadStatus
      }
    },

    /** 售后服务管理：更新处理状态（通过/驳回/介入完成） */
    SET_AFTERSALE_STATUS(state, { id, status }) {
      const a = state.aftersales.find(x => x.id === Number(id))
      if (a) {
        a.status = status
        if (status === '已完成') a.finishTime = nowStr()
      }
    },

    /** 采购竞价：认领/分配负责人 */
    SET_BIDDING_OWNER(state, { id, owner }) {
      const b = state.biddings.find(x => x.id === Number(id))
      if (b) b.owner = owner
    },

    /** 询价单：指派供应商 */
    SET_INQUIRY_ASSIGN(state, { id, vendor }) {
      const i = state.inquiries.find(x => x.id === Number(id))
      if (i) { i.assignedVendor = vendor; if (i.status === '待报价') i.status = '已报价' }
    },

    /** 消息中心：标记已读 / 全部已读 */
    SET_MESSAGE_READ(state, { id, all }) {
      if (all) {
        state.messages.forEach(m => { m.read = true })
      } else {
        const m = state.messages.find(x => x.id === Number(id))
        if (m) m.read = true
      }
    },

    /** 消息中心：平台主动发送站内 PM（支持多个收件人，逐条写入） */
    SEND_MESSAGE(state, list) {
      const maxId = state.messages.reduce((mx, m) => Math.max(mx, Number(m.id) || 0), 0)
      let seq = maxId
      list.forEach(m => {
        seq += 1
        state.messages.unshift({
          id: seq,
          title: m.title,
          content: m.content,
          type: m.type || '通知',
          module: m.module || '消息中心',
          recipient: m.recipient || '',
          sender: m.sender || '平台管理员',
          relatedPath: m.relatedPath || '',
          time: nowStr(),
          read: true, // 发送方（平台）视为已读，不计入未读徽章
        })
      })
    },

    /** 角色权限：新增/编辑/删除 */
    UPSERT_ROLE(state, role) {
      const idx = state.roles.findIndex(r => r.id === role.id)
      if (idx >= 0) state.roles.splice(idx, 1, { ...role })
      else state.roles.push({ ...role })
    },
    REMOVE_ROLE(state, id) {
      state.roles = state.roles.filter(r => r.id !== Number(id))
    },

    /** 菜单：按角色过滤后整体替换（登录/刷新时调用） */
    SET_MENU_LIST(state, tree) {
      state.menuList = tree
    },

    /** 账号：新增/编辑 */
    UPSERT_ACCOUNT(state, acc) {
      const idx = state.accounts.findIndex(a => a.id === acc.id)
      if (idx >= 0) state.accounts.splice(idx, 1, { ...state.accounts[idx], ...acc })
      else state.accounts.push({ ...acc })
    },
    /** 账号：删除 */
    REMOVE_ACCOUNT(state, id) {
      state.accounts = state.accounts.filter(a => a.id !== Number(id))
    },
    /** 账号：启用/停用 */
    SET_ACCOUNT_STATUS(state, { id, status }) {
      const a = state.accounts.find(x => x.id === Number(id))
      if (a) a.status = status
    },
    /** 账号：重置密码 */
    RESET_ACCOUNT_PASSWORD(state, { id, password }) {
      const a = state.accounts.find(x => x.id === Number(id))
      if (a) a.password = password
    },

    /** 品牌人格化配置（原则1） */
    UPDATE_BRAND_CONFIG(state, cfg) {
      state.brandConfig = { ...state.brandConfig, ...cfg }
    },

    /* ============ 客服系统（M1） mutations ============ */

    /** 客服档案：新增/编辑 */
    UPSERT_SERVICE_AGENT(state, agent) {
      const idx = state.serviceAgents.findIndex(a => a.id === agent.id)
      if (idx >= 0) state.serviceAgents.splice(idx, 1, { ...state.serviceAgents[idx], ...agent })
      else state.serviceAgents.push({ id: Date.now(), todayCount: 0, ...agent })
    },
    /** 客服档案：删除 */
    REMOVE_SERVICE_AGENT(state, id) {
      state.serviceAgents = state.serviceAgents.filter(a => a.id !== Number(id))
    },
    /** 客服档案：启用/停用/请假 */
    SET_SERVICE_AGENT_STATUS(state, { id, status }) {
      const a = state.serviceAgents.find(x => x.id === Number(id))
      if (a) a.status = status
    },
    /** 客服档案：重置今日接待数 */
    RESET_SERVICE_AGENT_COUNT(state, id) {
      const a = state.serviceAgents.find(x => x.id === Number(id))
      if (a) a.todayCount = 0
    },

    /** 排班：新增/编辑单条 */
    UPSERT_SCHEDULE(state, sch) {
      const idx = state.serviceSchedules.findIndex(s => s.id === sch.id)
      if (idx >= 0) state.serviceSchedules.splice(idx, 1, { ...sch })
      else state.serviceSchedules.push({ id: Date.now(), swapWith: null, swapStatus: '', leaveReason: '', ...sch })
    },
    /** 排班：删除单条 */
    REMOVE_SCHEDULE(state, id) {
      state.serviceSchedules = state.serviceSchedules.filter(s => s.id !== Number(id))
    },
    /** 排班：审批换班（approve/reject） */
    SET_SCHEDULE_SWAP(state, { id, approve, targetId }) {
      const s = state.serviceSchedules.find(x => x.id === Number(id))
      if (!s) return
      if (approve) {
        // 通过：与对方互换日期（对方原 schedule 取该日期）
        const other = state.serviceSchedules.find(x => x.id === targetId)
        if (other) {
          const tmpShift = s.shift
          s.shift = other.shift
          other.shift = tmpShift
          s.status = 'normal'; s.swapStatus = 'approved'; s.swapWith = null
          other.status = 'normal'; other.swapStatus = 'approved'; other.swapWith = null
        }
      } else {
        s.status = 'normal'; s.swapStatus = 'rejected'; s.swapWith = null
      }
    },
    /** 排班：标记请假（按日期区间批量写入 leave） */
    SET_SCHEDULE_LEAVE(state, { agentId, dates, reason }) {
      dates.forEach(date => {
        const exist = state.serviceSchedules.find(s => s.agentId === Number(agentId) && s.date === date)
        if (exist) { exist.status = 'leave'; exist.leaveReason = reason || '请假' }
        else state.serviceSchedules.push({ id: Date.now() + Math.floor(Math.random() * 1000), agentId: Number(agentId), date, shift: 'fullday', status: 'leave', swapWith: null, swapStatus: '', leaveReason: reason || '请假' })
      })
    },

    /** 分配日志：写入一条（用户侧扫码联系） */
    ADD_SERVICE_ASSIGN_LOG(state, log) {
      const maxId = state.serviceAssignLogs.reduce((mx, l) => Math.max(mx, Number(l.id) || 0), 0)
      state.serviceAssignLogs.unshift({ id: maxId + 1, watermarkFlag: 1, ...log })
      // 被分配客服 todayCount +1（轮询降权）
      const a = state.serviceAgents.find(x => x.id === Number(log.agentId))
      if (a) a.todayCount = (a.todayCount || 0) + 1
    },

    /** 轮询策略配置：更新 */
    UPDATE_SERVICE_CONFIG(state, cfg) {
      state.serviceConfig = { ...state.serviceConfig, ...cfg }
    },

    /* ============ 帮助中心（M2）：原则3 内容运营自助化 ============ */
    /** 分类：新增/更新（按 id 判定） */
    UPSERT_HELP_CATEGORY(state, cat) {
      const idx = state.helpCategories.findIndex(c => c.id === cat.id)
      if (idx >= 0) state.helpCategories.splice(idx, 1, { ...state.helpCategories[idx], ...cat })
      else state.helpCategories.push({ id: cat.id || (Date.now() + Math.floor(Math.random() * 1000)), ...cat })
    },
    /** 分类：删除（递归删子分类 + 其下文章标记未分类） */
    REMOVE_HELP_CATEGORY(state, id) {
      const removeIds = []
      const collect = (pid) => {
        removeIds.push(pid)
        state.helpCategories.filter(c => c.parentId === pid).forEach(c => collect(c.id))
      }
      collect(id)
      state.helpCategories = state.helpCategories.filter(c => !removeIds.includes(c.id))
      state.helpArticles.forEach(a => { if (removeIds.includes(a.categoryId)) a.categoryId = 0 })
    },
    /** 文章：新增/更新 */
    UPSERT_HELP_ARTICLE(state, art) {
      const idx = state.helpArticles.findIndex(a => a.id === art.id)
      if (idx >= 0) state.helpArticles.splice(idx, 1, { ...state.helpArticles[idx], ...art })
      else state.helpArticles.push({ id: art.id || (Date.now() + Math.floor(Math.random() * 1000)), status: 'draft', views: 0, helpful: 0, unhelpful: 0, ...art })
    },
    /** 文章：删除 */
    REMOVE_HELP_ARTICLE(state, id) {
      state.helpArticles = state.helpArticles.filter(a => a.id !== id)
    },
    /** 文章：状态流转（草稿→发布/下线） */
    SET_HELP_ARTICLE_STATUS(state, { id, status }) {
      const a = state.helpArticles.find(x => x.id === id)
      if (a) { a.status = status; if (status === 'published') a.publishedAt = a.publishedAt || nowStr() }
    },
    /** 文章：点赞/踩（用户侧反馈） */
    FEEDBACK_HELP_ARTICLE(state, { id, type }) {
      const a = state.helpArticles.find(x => x.id === id)
      if (a) { if (type === 'up') a.helpful = (a.helpful || 0) + 1; else a.unhelpful = (a.unhelpful || 0) + 1 }
    },
    /** 反馈：新增一条（用户侧提交） */
    ADD_HELP_FEEDBACK(state, fb) {
      const maxId = state.helpFeedbacks.reduce((mx, f) => Math.max(mx, Number(f.id) || 0), 0)
      state.helpFeedbacks.unshift({ id: maxId + 1, createdAt: nowStr(), ...fb })
    },

    /* ============ M3 页面引导 ============ */
    UPSERT_GUIDE(state, cfg) {
      const maxId = state.guideConfigs.reduce((mx, c) => Math.max(mx, Number(c.id) || 0), 0)
      const idx = state.guideConfigs.findIndex(c => c.id === cfg.id)
      const row = { updatedAt: nowStr(), ...cfg }
      if (idx >= 0) state.guideConfigs.splice(idx, 1, { ...state.guideConfigs[idx], ...row })
      else state.guideConfigs.unshift({ id: maxId + 1, createdAt: nowStr(), ...row })
    },
    REMOVE_GUIDE(state, id) { state.guideConfigs = state.guideConfigs.filter(c => c.id !== id) },
    SET_GUIDE_STATUS(state, { id, status }) {
      const c = state.guideConfigs.find(x => x.id === id)
      if (c) c.status = status
    },

    /* ============ M4 结算异常工单 ============ */
    UPSERT_EXCEPTION(state, t) {
      const maxId = state.exceptionTickets.reduce((mx, x) => Math.max(mx, Number(x.id) || 0), 0)
      const idx = state.exceptionTickets.findIndex(x => x.id === t.id)
      if (idx >= 0) state.exceptionTickets.splice(idx, 1, { ...state.exceptionTickets[idx], ...t })
      else state.exceptionTickets.unshift({ id: maxId + 1, code: 'EX' + nowStr().slice(0, 10).replace(/-/g, '') + '-' + String(maxId + 1).padStart(3, '0'), createdAt: nowStr(), ...t })
    },
    SET_EXCEPTION_STATUS(state, { id, status, ...rest }) {
      const t = state.exceptionTickets.find(x => x.id === id)
      if (t) { t.status = status; Object.assign(t, rest); if (status === '已关单') t.closedAt = nowStr() }
    },

    /* ============ M5 签收凭证审核 ============ */
    UPSERT_VOUCHER(state, v) {
      const maxId = state.voucherReviews.reduce((mx, x) => Math.max(mx, Number(x.id) || 0), 0)
      const idx = state.voucherReviews.findIndex(x => x.id === v.id)
      if (idx >= 0) state.voucherReviews.splice(idx, 1, { ...state.voucherReviews[idx], ...v })
      else state.voucherReviews.unshift({ id: maxId + 1, uploadedAt: nowStr(), ...v })
    },
    SET_VOUCHER_STATUS(state, { id, status, reviewer, rejectReason, rejectTemplate }) {
      const v = state.voucherReviews.find(x => x.id === id)
      if (v) { v.status = status; v.reviewer = reviewer || ''; v.rejectReason = rejectReason || ''; v.rejectTemplate = rejectTemplate || ''; v.reviewedAt = status === '待审' ? '' : nowStr() }
    },

    /** 重置业务数据（登出时调用） */
    RESET_BIZ_DATA(state) {
      state.vendors = JSON.parse(JSON.stringify(seedVendors))
      state.products = JSON.parse(JSON.stringify(seedProducts))
      state.reportOrders = JSON.parse(JSON.stringify(seedReportOrders))
      state.reverseApps = JSON.parse(JSON.stringify(seedReverseApps))
      state.shipOrders = JSON.parse(JSON.stringify(seedShipOrders))
      state.qualifications = JSON.parse(JSON.stringify(seedQualifications))
      state.contracts = JSON.parse(JSON.stringify(seedContracts))
      state.contractTemplates = JSON.parse(JSON.stringify(seedContractTemplates))
      state.shutdowns = JSON.parse(JSON.stringify(seedShutdowns))
      state.invoices = JSON.parse(JSON.stringify(seedInvoices))
      state.aftersales = JSON.parse(JSON.stringify(seedAftersales))
      state.biddings = JSON.parse(JSON.stringify(seedBiddings))
      state.inquiries = JSON.parse(JSON.stringify(seedInquiries))
      state.messages = JSON.parse(JSON.stringify(seedMessages))
      state.logs = JSON.parse(JSON.stringify(seedLogs))
      state.logSeq = 0
      state.serviceAgents = JSON.parse(JSON.stringify(seedServiceAgents))
      state.serviceSchedules = JSON.parse(JSON.stringify(seedServiceSchedules))
      state.serviceAssignLogs = JSON.parse(JSON.stringify(seedServiceAssignLogs))
      state.serviceConfig = JSON.parse(JSON.stringify(seedServiceConfig))
      state.helpCategories = JSON.parse(JSON.stringify(seedHelpCategories))
      state.helpArticles = JSON.parse(JSON.stringify(seedHelpArticles))
      state.helpFeedbacks = JSON.parse(JSON.stringify(seedHelpFeedbacks))
      state.guideConfigs = JSON.parse(JSON.stringify(seedGuideConfigs))
      state.exceptionTickets = JSON.parse(JSON.stringify(seedExceptionTickets))
      state.exceptionTypes = JSON.parse(JSON.stringify(seedExceptionTypes))
      state.exceptionSla = JSON.parse(JSON.stringify(seedExceptionSla))
      state.voucherReviews = JSON.parse(JSON.stringify(seedVoucherReviews))
    },
  },

  actions: {
    /**
     * 根据 path 自动设置一级菜单高亮
     * 在路由变化时调用（直接访问 URL / 刷新 / 路由跳转 都要能定位）
     */
    syncMenuFromPath({ commit, state }, path) {
      const index = findParentIndex(state.menuList, path)
      if (index >= 0 && state.activeMenuIndex !== index) {
        commit('SET_ACTIVE_MENU_INDEX', index)
      }
    },

    /** 登录（校验账号密码 + 角色绑定 + 构建角色菜单） */
    login({ commit, state }, payload) {
      const account = payload.username || payload.account
      const password = payload.password
      const acc = state.accounts.find(a => a.account === account && a.password === password)
      if (!acc) return Promise.reject(new Error('账号或密码错误'))
      if (acc.status === 'disabled') return Promise.reject(new Error('该账户已被停用，请联系系统管理员'))
      const token = 'manager-token-' + acc.id + '-' + Date.now()
      setToken(token)
      const role = state.roles.find(r => r.key === acc.roleKey)
      const info = {
        id: acc.id,
        account: acc.account,
        name: acc.name,
        roleId: role ? role.id : null,
        roleKey: acc.roleKey,
        roleName: acc.roleName,
        adminLevel: acc.adminLevel,
        dataScope: acc.dataScope,
        isProtected: acc.isProtected,
        avatar: '',
        phone: acc.phone,
        email: acc.email,
        company: '安徽亿佳供应链集团',
      }
      commit('SET_TOKEN', token)
      commit('SET_USER_INFO', info)
      try { window.sessionStorage.setItem('manager_user', JSON.stringify(info)) } catch (e) { /* ignore */ }
      commit('SET_MENU_LIST', buildMenuTree(role ? role.menuPaths : ['/dashboard']))
      commit('SET_ACTIVE_MENU_INDEX', 0)
      commit('ADD_LOG', {
        module: '系统',
        action: '登录',
        target: acc.name,
        detail: '登录成功（角色：' + (role ? role.name : acc.roleKey) + '）',
      })
      return Promise.resolve()
    },

    /** 登出 */
    logout({ commit }) {
      removeToken()
      commit('SET_TOKEN', '')
      commit('SET_USER_INFO', { name: '', avatar: '', phone: '', company: '' })
      commit('SET_MENU_LIST', menuTree)
      commit('DEL_ALL_VISITED_VIEWS')
      // 重置业务数据到初始态（避免下次登录串数据）
      commit('RESET_BIZ_DATA')
      try {
        window.sessionStorage.removeItem(VISITED_VIEWS_KEY)
        window.sessionStorage.removeItem('manager_user')
      } catch (e) {
        // ignore
      }
      return Promise.resolve()
    },

    /** 追加操作日志 */
    addLog({ commit }, log) {
      commit('ADD_LOG', log)
    },

    /** 添加一个已访问的页面标签 */
    addView({ commit }, view) {
      if (!view || (view.meta && view.meta.hidden)) return
      const exists = this.state.visitedViews.some(v => v.path === view.path)
      if (exists) return
      commit('ADD_VISITED_VIEW', view)
    },

    /** 关闭指定标签 */
    delView({ commit, state }, view) {
      return new Promise(resolve => {
        commit('DEL_VISITED_VIEW', view)
        resolve([...state.visitedViews])
      })
    },

    /** 关闭其他标签 */
    delOthersViews({ commit, state }, view) {
      return new Promise(resolve => {
        commit('DEL_OTHERS_VISITED_VIEWS', view)
        resolve([...state.visitedViews])
      })
    },

    /** 关闭所有标签 */
    delAllViews({ commit, state }) {
      return new Promise(resolve => {
        commit('DEL_ALL_VISITED_VIEWS')
        resolve([...state.visitedViews])
      })
    },
  },
})
